import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Heart, Users, Trophy, Target, ExternalLink, BookOpen, Car, Star, Zap, CircleDot, DollarSign, Shield, Truck, Apple, ClipboardList, Package, UserCog } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../context/LanguageContext";
import { usePageContent } from "../hooks/usePageContent";

const GOFUNDME_URL = "https://gofund.me/274183352";
const IMAGES = {
  training: "https://customer-assets.emergentagent.com/job_4e563cac-59c1-451d-b6a2-231b424045b8/artifacts/gkpyigf5_image.png",
};

const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold, rootMargin: '50px' });
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);
  return { ref, isVisible };
};

const AnimatedSection = ({ children, className = "", animation = "fly-up", delay = 0 }) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const baseTransform = { 'fly-left': 'translateX(-80px)', 'fly-right': 'translateX(80px)', 'fly-up': 'translateY(60px)', 'fly-down': 'translateY(-60px)', 'zoom': 'scale(0.8)' };
  const style = { opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateX(0) translateY(0) scale(1)' : baseTransform[animation] || 'translateY(60px)', transition: `opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms, transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms` };
  return <div ref={ref} className={className} style={style}>{children}</div>;
};

const AboutPage = () => {
  const { lang, t } = useLanguage();
  const { get } = usePageContent("about");
  const values = [
    { icon: Heart, ...t.about.passion },
    { icon: Users, ...t.about.community },
    { icon: BookOpen, ...t.about.education },
    { icon: Trophy, ...t.about.excellence }
  ];
  const provideTop = [
    { icon: Trophy, ...t.about.fullEquipment },
    { icon: Target, ...t.about.proCoaching }
  ];
  const provideBottom = [
    { icon: BookOpen, ...t.about.academicSupport },
    { icon: Car, ...t.about.transportation }
  ];
  const philosophy = [
    { num: "1", ...t.about.philPlay, color: "from-green-400 to-emerald-500" },
    { num: "2", ...t.about.philTrain, color: "from-blue-400 to-cyan-500" },
    { num: "3", ...t.about.philSucceed, color: "from-yellow-400 to-orange-500" }
  ];
  const operations = [
    { icon: Users, ...t.about.opsProfiles },
    { icon: ClipboardList, ...t.about.opsAcademic },
    { icon: Truck, ...t.about.opsAttendance },
    { icon: Apple, ...t.about.opsNutrition }
  ];
  const inventory = [
    { icon: Package, ...t.about.invGear },
    { icon: Shield, ...t.about.invSponsor }
  ];
  const personnel = [
    { icon: UserCog, ...t.about.persCoach, subtitle: t.about.persCoach.name },
    { icon: Users, title: t.about.persAssistant.title, desc: t.about.persAssistant.desc },
    { icon: Heart, title: t.about.persDonors.title, desc: t.about.persDonors.desc }
  ];

  const title = lang === "es" ? "Nosotros | Curious 123 Programa de Futbol Juvenil" : "About Us | Curious 123 Youth Soccer Program";
  const desc = lang === "es" ? "Conoce al Coach Jorge Luis Carcamo y la mision de Curious 123 para transformar vidas a traves del futbol." : "Learn about Coach Jorge Luis Carcamo and the Curious 123 mission to transform lives through soccer.";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href="https://curious123.org/about" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content="https://curious123.org/about" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Curious 123" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png" />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#F0F9FF] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-up" className="text-center max-w-4xl mx-auto">
            <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">{t.about.badge}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black leading-tight mb-6 font-['Kanit']">{get("hero_title", t.about.title)}</h1>
            <p className="text-lg text-gray-700">{get("hero_subtitle", t.about.subtitle)}</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Coach Bio */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <AnimatedSection animation="fly-left">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img src={get("story_image", IMAGES.training)} alt="Coach Jorge Luis Carcamo" className="w-full h-[500px] object-cover" />
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fly-right" delay={200}>
              <span className="inline-block bg-[#87CEEB]/20 text-black px-4 py-2 rounded-full text-sm font-medium mb-6">{t.about.meetFounder}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Kanit']">{t.about.coachName}</h2>
              <div className="space-y-4 text-gray-700">
                <p>{t.about.bio1}</p>
                <p>{t.about.bio2} <strong>{t.about.bio2Bold}</strong>{t.about.bio2End}</p>
                <p>{t.about.bio3} <strong>{t.about.bio3Bold}</strong> {t.about.bio3End}</p>
                <p className="text-[#87CEEB] font-semibold">{t.about.bio4}</p>
              </div>
              <div className="mt-8">
                <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="donate-btn rounded-full px-8 py-3 font-semibold uppercase tracking-wider">{t.about.supportBtn}<ExternalLink className="w-4 h-4 ml-2" /></Button>
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-[#F0F9FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-down" className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{t.about.valuesTitle}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t.about.valuesDesc}</p>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={value.title} className="animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                <Card className="bg-white border-gray-100 shadow-sm rounded-2xl overflow-hidden h-full hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-[#87CEEB] rounded-full flex items-center justify-center mx-auto mb-4"><value.icon className="w-8 h-8 text-black" /></div>
                    <h3 className="font-bold text-xl text-black mb-2 font-['Kanit']">{value.title}</h3>
                    <p className="text-gray-600">{value.desc}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Provide - Equipment & Coaching only */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-down" className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Kanit']">{get("provide_title", t.about.provideTitle)}</h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">{t.about.provideDesc}</p>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-8">
            {provideTop.map((item, index) => (
              <AnimatedSection key={item.title} animation={index % 2 === 0 ? "fly-left" : "fly-right"} delay={index * 100}>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#87CEEB] rounded-full flex items-center justify-center"><item.icon className="w-6 h-6 text-black" /></div>
                    <h3 className="font-bold text-xl font-['Kanit']">{item.title}</h3>
                  </div>
                  <ul className="space-y-2">{item.items.map(li => (<li key={li} className="flex items-center gap-2 text-gray-300"><Star className="w-4 h-4 text-[#87CEEB]" />{li}</li>))}</ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Futbol para Todos Mission + Academic & Transportation */}
      <section className="py-20 bg-white" data-testid="about-fpt-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-up" className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-black mb-6 font-['Kanit']">{t.about.fptTitle}</h2>
            <p className="text-lg sm:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              <span className="font-bold text-[#87CEEB]">{t.about.fptMission} </span>
              {t.about.fptDesc}
            </p>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {provideBottom.map((item, index) => (
              <AnimatedSection key={item.title} animation={index % 2 === 0 ? "fly-left" : "fly-right"} delay={index * 100}>
                <div className="bg-[#F0F9FF] rounded-2xl p-8 border border-[#87CEEB]/30">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#87CEEB] rounded-full flex items-center justify-center"><item.icon className="w-6 h-6 text-black" /></div>
                    <h3 className="font-bold text-xl text-black font-['Kanit']">{item.title}</h3>
                  </div>
                  <ul className="space-y-2">{item.items.map(li => (<li key={li} className="flex items-center gap-2 text-gray-700"><Star className="w-4 h-4 text-[#87CEEB]" />{li}</li>))}</ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Curious 123 Philosophy */}
      <section className="py-20 bg-[#F0F9FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-down" className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{t.about.philTitle}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t.about.philDesc}</p>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-8">
            {philosophy.map((p, index) => (
              <div key={p.num} className="animate-fade-in" style={{ animationDelay: `${index * 150}ms` }}>
                <Card className="bg-white border-gray-100 shadow-lg rounded-2xl overflow-hidden h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-8 text-center">
                    <div className={`w-16 h-16 bg-gradient-to-br ${p.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                      <span className="text-2xl font-bold text-white font-['Kanit']">{p.num}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-black mb-3 font-['Kanit']">{p.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{p.desc}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Operational Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-down" className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{t.about.opsTitle}</h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-6">
            {operations.map((op, index) => (
              <AnimatedSection key={op.title} animation={index % 2 === 0 ? "fly-left" : "fly-right"} delay={index * 100}>
                <Card className="bg-white border-gray-200 shadow-sm rounded-2xl h-full hover:shadow-lg transition-all">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#87CEEB] rounded-xl flex items-center justify-center flex-shrink-0"><op.icon className="w-6 h-6 text-black" /></div>
                    <div>
                      <h3 className="font-bold text-lg text-black mb-1 font-['Kanit']">{op.title}</h3>
                      <p className="text-gray-600 text-sm">{op.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Inventory & Equipment */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-down" className="text-center mb-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 font-['Kanit']">{t.about.invTitle}</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto italic">{t.about.invDesc}</p>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            {inventory.map((item, index) => (
              <AnimatedSection key={item.title} animation={index % 2 === 0 ? "fly-left" : "fly-right"} delay={index * 100}>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#87CEEB] rounded-full flex items-center justify-center"><item.icon className="w-6 h-6 text-black" /></div>
                    <h3 className="font-bold text-xl font-['Kanit']">{item.title}</h3>
                  </div>
                  <p className="text-gray-300">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Fundraising & Donor Transparency */}
      <section className="py-20 bg-[#F0F9FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-down" className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{t.about.fundTitle}</h2>
            <p className="text-lg text-gray-600">{t.about.fundIntegration}</p>
          </AnimatedSection>
          <div className="max-w-3xl mx-auto">
            <AnimatedSection animation="fly-up">
              <Card className="bg-white border-gray-100 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-8">
                  <h3 className="font-bold text-xl text-black mb-6 font-['Kanit']">{t.about.fundTiers}</h3>
                  <div className="space-y-4">
                    {[t.about.fundTier1, t.about.fundTier2, t.about.fundTier3].map((tier) => (
                      <div key={tier} className="flex items-center gap-3 p-4 bg-[#F0F9FF] rounded-xl">
                        <DollarSign className="w-6 h-6 text-[#87CEEB] flex-shrink-0" />
                        <span className="font-semibold text-black">{tier}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 p-4 border-t border-gray-100">
                    <div className="flex items-start gap-3">
                      <Zap className="w-6 h-6 text-[#87CEEB] flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-black font-['Kanit']">{t.about.fundImpact.title}</h4>
                        <p className="text-gray-600 text-sm">{t.about.fundImpact.desc}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 text-center">
                    <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer">
                      <Button className="donate-btn rounded-full px-8 py-3 font-semibold uppercase tracking-wider"><Heart className="w-4 h-4 mr-2" />{t.nav.donateNow}</Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Personnel & Roles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-down" className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{t.about.persTitle}</h2>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-6">
            {personnel.map((p, index) => (
              <div key={p.title} className="animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                <Card className="bg-white border-gray-200 shadow-sm rounded-2xl h-full hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-[#87CEEB] rounded-full flex items-center justify-center mx-auto mb-4"><p.icon className="w-8 h-8 text-black" /></div>
                    <h3 className="font-bold text-lg text-black mb-1 font-['Kanit']">{p.title}</h3>
                    {p.subtitle && <p className="text-[#87CEEB] font-semibold text-sm mb-2">{p.subtitle}</p>}
                    <p className="text-gray-600 text-sm">{p.desc}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Mission CTA */}
      <section className="py-20 bg-[#87CEEB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="zoom">
            <h2 className="text-3xl sm:text-4xl font-bold text-black mb-6 font-['Kanit']">{t.about.joinTitle}</h2>
            <p className="text-lg text-black/80 mb-8">{t.about.joinDesc}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-black text-white hover:bg-gray-900 rounded-full px-10 py-4 text-lg font-bold uppercase tracking-wider"><Heart className="w-5 h-5 mr-2" />{t.nav.donateNow}</Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full px-8 py-4 text-lg border-2 border-black text-black hover:bg-black hover:text-white transition-all">{t.about.contactUs}</Button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
